// ClientDocumentVaultPanel — blueprint module #7 Document Vault.
//
// One panel per client. Every document tied to that client, upload +
// classify + OCR + search + preview + delete. RLS keeps it scoped to
// the owning operator; storage is Supabase's private
// `client_documents` bucket.
//
// SETUP (one-time, per Supabase project):
//   1. Storage → New bucket → name: client_documents → PRIVATE
//   2. Run supabase_migrations/137_client_documents.sql in SQL editor
// Everything below runs off the standard operator JWT after that.
//
// Upload flow (two-step so we can send big files without Vercel's
// 4.5 MB body cap):
//   1. POST /api/documents/upload-url → { upload_url, storage_path }
//   2. PUT the file bytes directly to upload_url
//   3. POST /api/documents/register → row id
//   4. POST /api/documents/ocr (async, fire-and-forget)

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || body?.detail || `HTTP ${res.status}`);
  return body;
}

const DOC_TYPES = [
  ['drivers_license',      "Driver's license"],
  ['ssn_card',             'Social Security card'],
  ['marketplace_document', 'Marketplace document'],
  ['tax_form',             'Tax form'],
  ['proof_of_income',      'Proof of income'],
  ['proof_of_residency',   'Proof of residency'],
  ['application',          'Application'],
  ['policy_document',      'Policy document'],
  ['carrier_correspondence', 'Carrier correspondence'],
  ['other',                'Other'],
];

const OCR_STATUS_STYLE = {
  pending: { bg: 'bg-slate-100 text-slate-600', label: 'OCR queued' },
  running: { bg: 'bg-sky-100 text-sky-800',     label: 'OCR running' },
  ok:      { bg: 'bg-emerald-100 text-emerald-800', label: 'Searchable' },
  failed:  { bg: 'bg-rose-100 text-rose-800',   label: 'OCR failed' },
  skipped: { bg: 'bg-slate-100 text-slate-500', label: 'No OCR' },
};

function fmtBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ClientDocumentVaultPanel({ clientId, showToast = () => {} }) {
  const [rows, setRows]         = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [q, setQ]               = React.useState('');
  const [uploading, setUpload]  = React.useState(false);
  const [uploadPct, setPct]     = React.useState(0);
  const [missingTable, setMiss] = React.useState(false);
  const fileRef = React.useRef(null);
  const docTypeRef = React.useRef(null);

  const load = React.useCallback(async (searchTerm = q) => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);
    try {
      const path = `/api/documents/list?client_id=${encodeURIComponent(clientId)}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`;
      const data = await apiFetch(path);
      setRows(data.rows || []);
      setMiss(false);
    } catch (err) {
      if (/relation .* does not exist/i.test(err.message || '')) {
        setMiss(true);
      } else {
        showToast(`Documents load failed: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, q, showToast]);

  React.useEffect(() => { load(); }, [load]);

  // Debounce search — refire load 300ms after the user stops typing.
  React.useEffect(() => {
    const t = setTimeout(() => { load(q); }, 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || uploading) return;
    const docType = docTypeRef.current?.value || 'other';

    setUpload(true); setPct(0);
    try {
      // Step 1: get signed upload URL.
      const { upload_url, storage_path } = await apiFetch('/api/documents/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        }),
      });

      // Step 2: PUT the file bytes directly to Storage. Track progress
      // via XHR since fetch has no upload-progress API.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setPct(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`PUT ${xhr.status}: ${xhr.responseText.slice(0, 200)}`));
        };
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      // Step 3: register the metadata row.
      const { id } = await apiFetch('/api/documents/register', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          storage_path,
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
          doc_type: docType,
          run_ocr: true,
        }),
      });

      // Step 4: fire OCR in the background. Don't await — the UI
      // shows an "OCR running" chip and the list refresh polls.
      apiFetch('/api/documents/ocr', {
        method: 'POST',
        body: JSON.stringify({ id }),
      }).then(() => load(q)).catch(() => load(q));

      showToast('Document uploaded. OCR running in background.');
      fileRef.current.value = '';
      await load(q);
    } catch (err) {
      showToast(`Upload failed: ${err.message}`, 'error');
    } finally {
      setUpload(false); setPct(0);
    }
  };

  const openDownload = async (row) => {
    try {
      const { url } = await apiFetch(`/api/documents/download-url?id=${encodeURIComponent(row.id)}`);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast(`Download failed: ${err.message}`, 'error');
    }
  };

  const deleteRow = async (row) => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Delete ${row.filename}?`,
      body: 'This cannot be undone.',
      confirmLabel: 'Delete document', danger: true,
    }) : Promise.resolve(window.confirm(`Delete ${row.filename}?`)));
    if (!ok) return;
    try {
      await apiFetch('/api/documents/delete', {
        method: 'POST',
        body: JSON.stringify({ id: row.id }),
      });
      showToast('Document deleted.');
      setRows(rs => rs.filter(r => r.id !== row.id));
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  if (missingTable) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-2.5 text-xs text-amber-900">
        <strong>Document Vault not yet migrated.</strong> Run
        {' '}<code className="font-mono">supabase_migrations/137_client_documents.sql</code>{' '}
        AND create a private Storage bucket named{' '}<code className="font-mono">client_documents</code>{' '}
        to enable this panel. Blueprint module #7.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Upload row */}
      <form onSubmit={onUpload} className="flex flex-wrap items-center gap-2 border border-slate-200 bg-white rounded-lg p-3">
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.tif,.tiff,.txt,.doc,.docx"
          className="text-[12px] flex-1 min-w-[180px]"
          disabled={uploading}
        />
        <select
          ref={docTypeRef}
          defaultValue="other"
          className="text-[12px] border border-slate-300 rounded-md px-2 py-1.5"
          disabled={uploading}
        >
          {DOC_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <button
          type="submit"
          disabled={uploading}
          className="text-[12px] font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-md"
        >
          {uploading ? `Uploading ${uploadPct}%` : '⬆ Upload'}
        </button>
      </form>

      {/* Search row */}
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="🔍 Search filename, description, or OCR text"
        className="w-full text-[12.5px] border border-slate-300 rounded-md px-3 py-2 focus:border-emerald-500 focus:outline-none"
      />

      {/* Doc list */}
      {loading && rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-1 py-2">Loading documents…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic px-1 py-3">
          {q ? 'No matches for that search.' : 'No documents on file yet. Upload above.'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map(r => {
            const typeLabel = DOC_TYPES.find(([v]) => v === r.doc_type)?.[1] || 'Other';
            const style = OCR_STATUS_STYLE[r.ocr_status] || OCR_STATUS_STYLE.pending;
            return (
              <div key={r.id} className="border border-slate-200 rounded-lg p-3 hover:border-slate-300 bg-white">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <button
                        onClick={() => openDownload(r)}
                        className="text-[13px] font-semibold text-emerald-700 hover:underline"
                      >
                        📄 {r.filename}
                      </button>
                      <span className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{typeLabel}</span>
                      <span className={`text-[9.5px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded ${style.bg}`}>
                        {style.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {new Date(r.created_at).toLocaleString()} · {fmtBytes(r.size_bytes)} · {r.mime_type}
                    </div>
                    {r.description && (
                      <div className="text-[11.5px] text-slate-700 mt-1">{r.description}</div>
                    )}
                    {r.ocr_snippet && (
                      <div className="text-[11px] text-slate-800 mt-1.5 bg-amber-50 border-l-2 border-amber-400 px-2 py-1 rounded">
                        …{r.ocr_snippet}…
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      onClick={() => openDownload(r)}
                      className="text-[10.5px] font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => deleteRow(r)}
                      className="text-[10.5px] font-semibold text-rose-600 hover:text-rose-800 border border-rose-200 rounded-md px-2 py-1"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
